import { createReducer, on } from '@ngrx/store';
import {
  CopilotActiveRun,
  CopilotExecutionStatus,
  Conversation,
  Message,
  MessageRole,
} from '../models/copilot.models';
import { CopilotActions } from './copilot.actions';

export interface CopilotClientState {
  conversations: Conversation[];
  messages: Record<string, Message[]>;
  selectedConversationId: string | null;
  lastActiveConversationId?: string;
  lastArchivedConversationId?: string;
  activeRuns: Record<string, CopilotActiveRun | undefined>;
  executionStatus: Record<string, CopilotExecutionStatus>;
  clientGuidByConversation: Record<string, string>;
  agentIdentifierByConversation: Record<string, string>;
  inputTextByConversation: Record<string, string>;
  chatSearchByConversation?: Record<
    string,
    { query: string; activeMatchIndex: number }
  >;
}

export interface CopilotState {
  clients: Record<string, CopilotClientState>;
  selectedClientId: string | null;
  highlightedClientIds: Set<string>;
  highlightedConversations: Set<string>;
}

const initialState: CopilotState = {
  clients: {},
  selectedClientId: null,
  highlightedClientIds: new Set(),
  highlightedConversations: new Set(),
};

export function defaultClientState(): CopilotClientState {
  return {
    conversations: [],
    messages: {},
    selectedConversationId: null,
    activeRuns: {},
    executionStatus: {},
    clientGuidByConversation: {},
    agentIdentifierByConversation: {},
    inputTextByConversation: {},
    chatSearchByConversation: {},
  };
}

function clientState(state: CopilotState, clientId: string): CopilotClientState {
  return state.clients[clientId] ?? defaultClientState();
}

function withClient(
  state: CopilotState,
  clientId: string,
  client: CopilotClientState
): CopilotState {
  return { ...state, clients: { ...state.clients, [clientId]: client } };
}

export const copilotReducer = createReducer(
  initialState,

  on(CopilotActions.selectClient, (state, { clientId }) => ({
    ...state,
    selectedClientId: clientId,
  })),

  on(
    CopilotActions.selectConversation,
    (state, { clientId, conversationId, view }) => {
      const client = clientState(state, clientId);
      return withClient(state, clientId, {
        ...client,
        selectedConversationId: conversationId,
        ...(view === 'active'
          ? { lastActiveConversationId: conversationId }
          : {}),
        ...(view === 'archived'
          ? { lastArchivedConversationId: conversationId }
          : {}),
      });
    }
  ),

  on(
    CopilotActions.setConversationContext,
    (state, { clientId, conversationId, clientGuid }) => {
      const client = clientState(state, clientId);
      return withClient(state, clientId, {
        ...client,
        clientGuidByConversation: {
          ...client.clientGuidByConversation,
          [conversationId]: clientGuid,
        },
      });
    }
  ),

  on(
    CopilotActions.loadMessagesSuccess,
    (
      state,
      {
        clientId,
        conversationId,
        messages,
        executionStatus,
        activeRun,
        agentIdentifier,
      }
    ) => {
      const client = clientState(state, clientId);
      const activeRuns = { ...client.activeRuns };
      const agentIdentifierByConversation = {
        ...client.agentIdentifierByConversation,
      };
      if (activeRun) activeRuns[conversationId] = activeRun;
      else delete activeRuns[conversationId];
      if (agentIdentifier) {
        agentIdentifierByConversation[conversationId] = agentIdentifier;
      } else {
        delete agentIdentifierByConversation[conversationId];
      }
      return withClient(state, clientId, {
        ...client,
        messages: { ...client.messages, [conversationId]: messages },
        executionStatus: {
          ...client.executionStatus,
          [conversationId]: executionStatus,
        },
        activeRuns,
        agentIdentifierByConversation,
      });
    }
  ),

  on(CopilotActions.messageReceived, (state, action) => {
    const client = clientState(state, action.clientId);
    const messages = upsertMessage(
      client.messages[action.conversationId] ?? [],
      action.message
    );
    return withClient(state, action.clientId, {
      ...client,
      messages: { ...client.messages, [action.conversationId]: messages },
    });
  }),

  on(CopilotActions.sendMessage, (state, action) => {
    const client = clientState(state, action.clientId);
    const inputTextByConversation = { ...client.inputTextByConversation };
    delete inputTextByConversation[action.conversationId];
    return withClient(state, action.clientId, {
      ...client,
      activeRuns: {
        ...client.activeRuns,
        [action.conversationId]: {
          runId: 'pending',
          streamUrl: '',
          status: 'submitting',
          attempt: 0,
          startedAt: action.startedAt,
        },
      },
      executionStatus: {
        ...client.executionStatus,
        [action.conversationId]: 'queued',
      },
      inputTextByConversation,
      ...(action.clientGuid
        ? {
            clientGuidByConversation: {
              ...client.clientGuidByConversation,
              [action.conversationId]: action.clientGuid,
            },
          }
        : {}),
    });
  }),

  on(CopilotActions.submissionAccepted, (state, action) => {
    const client = clientState(state, action.clientId);
    return withClient(state, action.clientId, {
      ...client,
      activeRuns: {
        ...client.activeRuns,
        [action.conversationId]: action.activeRun,
      },
      executionStatus: {
        ...client.executionStatus,
        [action.conversationId]: 'running',
      },
    });
  }),

  on(CopilotActions.runConnectionChanged, (state, action) => {
    const client = clientState(state, action.clientId);
    const activeRun = client.activeRuns[action.conversationId];
    if (!activeRun) return state;
    return withClient(state, action.clientId, {
      ...client,
      activeRuns: {
        ...client.activeRuns,
        [action.conversationId]: {
          ...activeRun,
          status: action.status,
          attempt: action.attempt,
          ...(action.lastEventId
            ? { lastEventId: action.lastEventId }
            : {}),
          ...(action.error ? { error: action.error } : {}),
        },
      },
    });
  }),

  on(CopilotActions.runSnapshotReceived, (state, action) => {
    const client = clientState(state, action.clientId);
    const current = client.messages[action.conversationId] ?? [];
    const messages = [
      ...current.filter(
        (message) =>
          !(
            message.role === MessageRole.ASSISTANT &&
            message.metadata?.['run_id'] === action.runId
          )
      ),
      ...action.messages,
    ];
    const activeRun = client.activeRuns[action.conversationId];
    return withClient(state, action.clientId, {
      ...client,
      messages: { ...client.messages, [action.conversationId]: messages },
      activeRuns: activeRun
        ? {
            ...client.activeRuns,
            [action.conversationId]: {
              ...activeRun,
              lastEventId: action.lastEventId,
              ...(action.error ? { error: action.error } : {}),
            },
          }
        : client.activeRuns,
    });
  }),

  on(CopilotActions.runReconciled, (state, action) => {
    const client = clientState(state, action.clientId);
    const activeRuns = { ...client.activeRuns };
    delete activeRuns[action.conversationId];
    return withClient(state, action.clientId, {
      ...client,
      messages: {
        ...client.messages,
        [action.conversationId]: action.messages,
      },
      activeRuns,
      executionStatus: {
        ...client.executionStatus,
        [action.conversationId]: action.executionStatus,
      },
    });
  }),

  on(CopilotActions.runFailed, (state, action) => {
    const client = clientState(state, action.clientId);
    const activeRuns = { ...client.activeRuns };
    delete activeRuns[action.conversationId];
    const messages = action.optimisticUserId
      ? (client.messages[action.conversationId] ?? []).filter(
          (message) => message.id !== action.optimisticUserId
        )
      : client.messages[action.conversationId] ?? [];
    return withClient(state, action.clientId, {
      ...client,
      messages: { ...client.messages, [action.conversationId]: messages },
      activeRuns,
      executionStatus: {
        ...client.executionStatus,
        [action.conversationId]: 'idle',
      },
    });
  }),

  on(CopilotActions.runDisconnected, (state, action) => {
    const client = clientState(state, action.clientId);
    const activeRun = client.activeRuns[action.conversationId];
    if (!activeRun) return state;
    return withClient(state, action.clientId, {
      ...client,
      activeRuns: {
        ...client.activeRuns,
        [action.conversationId]: {
          ...activeRun,
          status: 'disconnected',
          error: action.error,
        },
      },
    });
  }),

  on(CopilotActions.updateInputText, (state, action) => {
    const client = clientState(state, action.clientId);
    return withClient(state, action.clientId, {
      ...client,
      inputTextByConversation: {
        ...client.inputTextByConversation,
        [action.conversationId]: action.text,
      },
    });
  }),

  on(CopilotActions.clearInputText, (state, action) => {
    const client = clientState(state, action.clientId);
    const inputTextByConversation = { ...client.inputTextByConversation };
    delete inputTextByConversation[action.conversationId];
    return withClient(state, action.clientId, {
      ...client,
      inputTextByConversation,
    });
  }),

  on(CopilotActions.triggerClientHighlight, (state, { clientId }) => {
    const highlightedClientIds = new Set(state.highlightedClientIds);
    highlightedClientIds.add(clientId);
    return { ...state, highlightedClientIds };
  }),

  on(CopilotActions.clearClientHighlight, (state, { clientId }) => {
    const highlightedClientIds = new Set(state.highlightedClientIds);
    highlightedClientIds.delete(clientId);
    return { ...state, highlightedClientIds };
  }),

  on(CopilotActions.clearClient, (state, { clientId }) => {
    const clients = { ...state.clients };
    delete clients[clientId];
    const highlightedClientIds = new Set(state.highlightedClientIds);
    highlightedClientIds.delete(clientId);
    return {
      ...state,
      clients,
      highlightedClientIds,
      selectedClientId:
        state.selectedClientId === clientId ? null : state.selectedClientId,
    };
  }),

  on(
    CopilotActions.triggerConversationHighlight,
    (state, { clientId, conversationId }) => {
      const highlightedConversations = new Set(state.highlightedConversations);
      highlightedConversations.add(`${clientId}:${conversationId}`);
      return { ...state, highlightedConversations };
    }
  ),

  on(
    CopilotActions.clearConversationHighlight,
    (state, { clientId, conversationId }) => {
      const highlightedConversations = new Set(state.highlightedConversations);
      highlightedConversations.delete(`${clientId}:${conversationId}`);
      return { ...state, highlightedConversations };
    }
  ),

  on(CopilotActions.updateChatSearchQuery, (state, action) => {
    const client = state.clients[action.clientId];
    if (!client) return state;
    return withClient(state, action.clientId, {
      ...client,
      chatSearchByConversation: {
        ...(client.chatSearchByConversation ?? {}),
        [action.conversationId]: { query: action.query, activeMatchIndex: 0 },
      },
    });
  }),

  on(CopilotActions.setChatSearchActiveMatchIndex, (state, action) => {
    const client = state.clients[action.clientId];
    if (!client) return state;
    const query =
      client.chatSearchByConversation?.[action.conversationId]?.query ?? '';
    return withClient(state, action.clientId, {
      ...client,
      chatSearchByConversation: {
        ...(client.chatSearchByConversation ?? {}),
        [action.conversationId]: {
          query,
          activeMatchIndex: action.activeMatchIndex,
        },
      },
    });
  }),

  on(CopilotActions.clearChatSearch, (state, action) => {
    const client = state.clients[action.clientId];
    if (!client) return state;
    const chatSearchByConversation = {
      ...(client.chatSearchByConversation ?? {}),
    };
    delete chatSearchByConversation[action.conversationId];
    return withClient(state, action.clientId, {
      ...client,
      chatSearchByConversation,
    });
  })
);

function upsertMessage(messages: Message[], message: Message): Message[] {
  const index = messages.findIndex((item) => item.id === message.id);
  if (index < 0) return [...messages, message];
  const next = [...messages];
  next[index] = message;
  return next;
}
